// LIBRARIES
import React, { useEffect, useState } from "react";
import { useT, UT } from "@transifex/react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useHistory } from "react-router-dom";
import StyledButton from "../../../basic/styledButton/StyledButton";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import SelectSearch, { fuzzySearch } from "react-select-search";
import { UserSubject } from "p6m-p6u";

import { useGeneralContext } from "../../../context/AppGeneralContext";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import UserContentCover from "../../../basic/userContentCover/UserContentCover";
import styled from "styled-components";
import DeleteCardWarningModal from "../../../basic/create/deleteCardWarningModal/DeleteCardWarningModal";
import CreateInfoModal from "../../../basic/create/InfoModal/CreateInfoModal";
import CtasWrapper from "../../../wrapper/create/ctasWrapper/CtasWrapper";
import UnitAndCardsWrapperRT from "../../../complex/create/unitAndCardsWrapperRT/UnitAndCardsWrapperRT";
import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import TestCreationHeader from "../../../complex/create/testCreationHeader/TestCreationHeader";
import VocabTrainingCreationHeader from "../../../complex/create/vocabTrainingCreationHeader/VocabTrainingCreationHeader";
import VocabTrainingTitleBar from "../../../complex/create/vocabTrainingTitleBar/VocabTrainingTitleBar";
import { VocabTrainingTitleTags } from "../../../enums/vocabTrainingTitleTags";
import usePreviouslyUsedCards from "../../../hooks/usePreviouslyUsedCards";
import { MIN_WORDS_DEFAULT } from "../../../helpers/ExerciseTypesHelper";

import { Wrapper, MiddleContentWrapper, SmallSelectWrapper, SelectedContentWrapper } from "./StyledComponents";

export interface SelectOwnContentSourceProps {}

const BookHeaderInfo = styled.div`
    display: flex;
    width: 100%;
    justify-content: left;
    align-items: center;
    padding: 0.5rem 0.8rem;
    border-radius: 1rem;
    margin-bottom: 1rem;
    background: white;
`;

const BookImageWrapper = styled.div`
    //width: auto;
    margin-right: 1rem;
    height: 100%;
`;

const BookNameWrapper = styled.div`
    flex: 1;
    max-width: max(500px, 50%);
`;

const ExerciseToSpan = styled.span`
    font-size: 0.9rem;
`;

const SelectOwnContent: React.FC<SelectOwnContentSourceProps> = (props) => {
    const {
        selectedUserSubject,
        vocabularyDrawerContent,
        removeContentFromVocabularyDrawer,
        loadUserUnitsForSubject,
        userUnitCards,
        removeCardsFromSelectedWords,
        userOwnSubjects,
        userOwnSubjectUnits,
        shouldSubjectSelectionBeDisabled,
        openWarningModal,
        testName,
        selectedTitleTag,
        setSelectedTitleTag,
    } = useTestCreationContext();

    const { creatingMode } = useGeneralContext();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const isRecurringTaskMode = creatingMode === CreationModes.RECURRING_TASK;

    // Surface which cards are already assigned to a class for this own-content subject (recurring
    // task flow only). Mirrors the schoolbook vocabulary page.
    usePreviouslyUsedCards(isRecurringTaskMode ? selectedUserSubject?.id : undefined);

    const t = useT();
    const t_selectTitle = t("Please select a Content and Cards", { _tags: "CreateTest,SelectOwnContentSource" });
    const t_back = t("Back", { _tags: "CreateTest,SelectOwnContentSource" });
    const t_noResults = t("No Results", { _tags: "CreateTest,SelectOwnContentSource" });
    const t_exerciseTo = t("Exercise To", { _tags: "CreateTest,SelectOwnContentSource" });
    const t_continue = t("Continue", { _tags: "CreateTest,SelectOwnContentSource" });
    const t_chooseMinVocabulary = t("Please select at least {minWords} word(s)", {
        _tags: "CreateTest,SelectOwnContentSource",
        minWords: MIN_WORDS_DEFAULT,
    });

    const t_selectSubject = t("Select a Subject", { _tags: "CreateTest,SelectVocabularySource" });

    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedSubjectInfo, setSelectedSubjectInfo] = useState<UserSubject>({});

    const [currentWordForDeletion, setCurrentWordForDeletion] = useState("");
    const [isCurrentWordForDeletionModalOpen, setIsCurrentWordForDeletionModalOpen] = useState(false);

    const [currentUnitCardsForDeletion, setCurrentUnitCardsForDeletion] = useState<Array<string>>([]);
    const [isDeleteAllUnitCardsModalOpen, setIsDeleteAllUnitCardsModalOpen] = useState(false);

    const history = useHistory();

    useEffect(() => {
        if (selectedSubject) {
            if (selectedUserSubject.id !== selectedSubject && selectedSubject !== "") {
                removeContentFromVocabularyDrawer(creatingMode === CreationModes.TEST ? "OWN" : "ALL");
            }
            loadUserUnitsForSubject(selectedSubject);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedUserSubject?.id && selectedUserSubject.id !== selectedSubject) {
            setSelectedSubject(selectedUserSubject.id);
            setSelectedSubjectInfo(selectedUserSubject);
        } else if (
            selectedSubject &&
            (userOwnSubjectUnits.length === 0 || userOwnSubjectUnits[0]?.subjectId !== selectedSubject)
        ) {
            loadUserUnitsForSubject(selectedSubject);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserSubject]);

    const updateSubject = (selectedValue: any) => {
        setSelectedSubject(selectedValue as string);

        let currentSubject = userOwnSubjects.find((s) => s.id === selectedValue);
        if (currentSubject) {
            setSelectedSubjectInfo(currentSubject);
        }
    };

    function handleContinueButton() {
        if (creatingMode === CreationModes.RECURRING_TASK) {
            // Pre-select "Based on vocabulary" (resolves to the subject name for own content) unless
            // the user already chose a tag or typed a custom title. The title bar owns resolution.
            if (!selectedTitleTag && !testName) {
                setSelectedTitleTag(VocabTrainingTitleTags.BASED_ON_VOCAB);
            }
            history.push("/create/rt-summary");
        } else {
            if (Object.keys(vocabularyDrawerContent).length >= MIN_WORDS_DEFAULT) {
                history.push("/create/summary");
            } else {
                openWarningModal(t_chooseMinVocabulary);
            }
        }
    }

    function handleBackButton() {
        if (creatingMode === CreationModes.RECURRING_TASK) {
            history.push("/create/choose-vocab-training-source");
        } else {
            history.goBack();
        }
    }

    function openDeleteModalFn(wordId: string) {
        setCurrentWordForDeletion(wordId);
        setIsCurrentWordForDeletionModalOpen(true);
    }

    function openDeleteUnitCardsFn(cardsToDelete: Array<string>) {
        setCurrentUnitCardsForDeletion(cardsToDelete);
        setIsDeleteAllUnitCardsModalOpen(true);
    }

    function closeDeleteWordWarningModal() {
        setIsCurrentWordForDeletionModalOpen(false);
    }

    function deleteCardsFromUnit() {
        removeCardsFromSelectedWords(currentUnitCardsForDeletion);
        setIsDeleteAllUnitCardsModalOpen(false);
    }

    // In the vocab-training flow this page sits between sibling pages that all use
    // VocabTrainingCreationHeader; it must match them (cancel modal text, discard navigation)
    // rather than the test-flow header.
    const Header = isRecurringTaskMode ? VocabTrainingCreationHeader : TestCreationHeader;

    const renderFooter = () => {
        return (
            <CtasWrapper
                showScrollToTop
                isSticky
            >
                <ResponsiveButton
                    onClick={handleBackButton}
                    iconName="chevron-left"
                    label={t_back}
                >
                    <StyledButton
                        onClick={handleBackButton}
                        arrowDirection={"LEFT"}
                        buttonStyle={"BLANK"}
                    >
                        {t_back}
                    </StyledButton>
                </ResponsiveButton>
                <StyledButton
                    data-testid="button-continue-own-content"
                    onClick={handleContinueButton}
                    arrowDirection={"NONE"}
                    buttonStyle={"PRIMARY"}
                    disabled={Object.keys(vocabularyDrawerContent).length === 0}
                >
                    {t_continue}
                </StyledButton>
            </CtasWrapper>
        );
    };

    return (
        <Header hasFullHeightContent>
            <Wrapper>
                {isRecurringTaskMode && (
                    <CreationFlowSection>
                        <VocabTrainingTitleBar hideBookTag />
                    </CreationFlowSection>
                )}
                <p className={`${getStyleBasedOnBreakpoint(Breakpoint.MD, "px-s py-s")} text-l text-p6-grey-dark`}>
                    {t_selectTitle}
                </p>
                <BookHeaderInfo>
                    <BookImageWrapper>
                        <UserContentCover
                            primaryLanguage={selectedSubject !== "" ? selectedSubjectInfo.primaryLang! : "other"}
                            secondaryLanguage={selectedSubject !== "" ? selectedSubjectInfo.secondaryLang : ""}
                            small
                        />
                    </BookImageWrapper>
                    <BookNameWrapper>
                        <ExerciseToSpan>{t_exerciseTo}</ExerciseToSpan>
                        <SmallSelectWrapper data-testid="select-own-subject">
                            {!shouldSubjectSelectionBeDisabled && (
                                <SelectSearch
                                    options={userOwnSubjects.map((s) => {
                                        return { name: s.name!, value: s.id! };
                                    })}
                                    search
                                    filterOptions={fuzzySearch}
                                    emptyMessage={t_noResults}
                                    placeholder={t_selectSubject}
                                    value={selectedSubject}
                                    onChange={updateSubject}
                                    disabled={shouldSubjectSelectionBeDisabled}
                                />
                            )}
                            {shouldSubjectSelectionBeDisabled && <span>{selectedUserSubject.name}</span>}
                        </SmallSelectWrapper>
                    </BookNameWrapper>
                </BookHeaderInfo>
                <MiddleContentWrapper>
                    <SelectedContentWrapper>
                        {Object.entries(userUnitCards).map(([id, cards]) => {
                            let hasCards = cards.length;
                            let unit = userOwnSubjectUnits.find((u) => u.id === id);
                            return (
                                <UnitAndCardsWrapperRT
                                    unitContent={unit!}
                                    key={id}
                                    isUserOwnContent
                                    openDeleteModalFn={openDeleteModalFn}
                                    openDeleteUnitCardsFn={openDeleteUnitCardsFn}
                                    cardsList={hasCards ? cards : []}
                                    totalUnitCardsCount={hasCards ? cards.length : 0}
                                />
                            );
                        })}
                        {userOwnSubjectUnits.length === 0 && <p>{t_noResults}</p>}
                    </SelectedContentWrapper>
                </MiddleContentWrapper>
                <DeleteCardWarningModal
                    isModalOpen={isCurrentWordForDeletionModalOpen}
                    closeModalFn={closeDeleteWordWarningModal}
                    wordId={currentWordForDeletion}
                />
                <CreateInfoModal
                    isOpen={isDeleteAllUnitCardsModalOpen}
                    onRequestClose={() => {
                        setIsDeleteAllUnitCardsModalOpen(false);
                    }}
                    continueAction={deleteCardsFromUnit}
                    continueText={t_continue}
                    cancelAction={() => {
                        setIsDeleteAllUnitCardsModalOpen(false);
                    }}
                    compact
                >
                    <UT
                        _str="Warning: Unchecking this box deletes all words from the basket and exercises for which those words have been used."
                        _inline
                        _tags="CreateTest,SelectBookVocabulary"
                    />
                </CreateInfoModal>
            </Wrapper>
            {renderFooter()}
        </Header>
    );
};

export default SelectOwnContent;
